import { Button, Card, Select, Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiClientContext } from "../../../App";
import { ProjectListDTO, UsersListDTO } from "../types";

interface ReportsFiltersProps {
  onApply: (filters: { projectIds: string[]; employeeIds: string[] }) => void;
}

const ReportsFilters = ({ onApply }: ReportsFiltersProps) => {
  const location = useLocation();
  const [isFetchingFiltersOptions, setIsFetchingFiltersOptions] =
    useState(false);
  const [projects, setProjects] = useState<ProjectListDTO>([]);
  const [employees, setEmployees] = useState<UsersListDTO>([]);
  const initialParams = new URLSearchParams(location.search);
  const [selectedProjects, setSelectedProjects] = useState<string[]>(
    initialParams.get("projectIds")
      ? initialParams.get("projectIds")!.split(",")
      : [],
  );
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>(
    initialParams.get("employeeIds")
      ? initialParams.get("employeeIds")!.split(",")
      : [],
  );
  const { apiClient } = useContext(ApiClientContext);

  const getFiltersOptions = async () => {
    try {
      setIsFetchingFiltersOptions(true);
      const [projectsRes, employeesRes] = await Promise.all([
        apiClient.get("/api/projects/list"),
        apiClient.get("/api/users/list?status=20"),
      ]);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.warn(error);
    } finally {
      setIsFetchingFiltersOptions(false);
    }
  };

  useEffect(() => {
    getFiltersOptions();
  }, []);

  const hasActiveFilters =
    selectedProjects.length > 0 || selectedEmployees.length > 0;

  const handleProjectsChange = (values: string[]) => {
    setSelectedProjects(values);
    onApply({ projectIds: values, employeeIds: selectedEmployees });
  };

  const handleEmployeesChange = (values: string[]) => {
    setSelectedEmployees(values);
    onApply({ projectIds: selectedProjects, employeeIds: values });
  };

  const handleClear = () => {
    setSelectedProjects([]);
    setSelectedEmployees([]);
    onApply({ projectIds: [], employeeIds: [] });
  };

  return (
    <>
      <Card bodyStyle={{ padding: "12px 16px" }} style={{ marginBottom: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}
          >
            {isFetchingFiltersOptions ? (
              <>
                <Skeleton.Input active style={{ width: 200 }} />
                <Skeleton.Input active style={{ width: 200 }} />
              </>
            ) : (
              <>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Projects"
                  value={selectedProjects}
                  onChange={handleProjectsChange}
                  style={{ minWidth: 200, maxWidth: 300 }}
                  optionFilterProp="label"
                  options={projects.map((p) => ({
                    label: p.name,
                    value: p.id,
                  }))}
                />
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Employees"
                  value={selectedEmployees}
                  onChange={handleEmployeesChange}
                  style={{ minWidth: 200, maxWidth: 500 }}
                  optionFilterProp="label"
                  options={employees.map((e) => ({
                    label: `${e.firstName} ${e.lastName}`,
                    value: e.id,
                  }))}
                />
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleClear} disabled={!hasActiveFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default ReportsFilters;
